
/**
 * @class TASPositivePayCreateCheckTemplateForm
 * @classdesc Class definition for controlling a positive pay check template form
 * form which will then be transformed into a positive pay check template.
 * @property {STRING} _id - The id of the positive pay check template form.
 * @property {STRING} _positivePayCheckFile - The positive pay check file.
 * @property {STRING} _importAttachedFile - The import attached file.
 * @property {STRING} _name - The name of the positive pay check template form.
 * @property {STRING} _inputDecimalsIntoAmount - The input decimals into amount.
 * @property {STRING} _numberOfRowsToExcludeHeader - The number of rows to exclude header.
 * @property {STRING} _numberOfRowsToExcludeFooter - The number of rows to exclude footer.
 * @property {STRING} _textQualified - The text qualified.
 * @property {STRING} _fileFormatDelimited - The file format delimited.
 * @property {STRING} _fileFormatFixedWidth - The file format fixed width.
 * @property {STRING} _fileFormatDelimitedType - The file format delimited type.
 * @property {STRING} _mapColumn1 - The map column 1.
 * @property {STRING} _mapColumn2 - The map column 2.
 * @property {STRING} _mapColumn3 - The map column 3.
 * @property {STRING} _mapColumn4 - The map column 4.
 * @property {STRING} _mapColumn5 - The map column 5.
 * @property {STRING} _mapColumn6 - The map column 6.
 * @property {STRING} _mapColumn7 - The map column 7.
 * @property {STRING} _mapColumn8 - The map column 8.
 * @property {STRING} _itemTypeMappingIssue - The item type mapping issue.
 * @property {STRING} _itemTypeMappingStop - The item type mapping stop.
 * @property {STRING} _itemTypeMappingVoid - The item type mapping void.
 */
export default class TASPositivePayCreateCheckTemplateForm {
    constructor(record) {
        this.id = record.id;
        this.positivePayCheckFile = record.positivePayCheckFile;
        this.importAttachedFile = record.importAttachedFile;
        this.name = record.name;
        this.inputDecimalsIntoAmount = record.inputDecimalsIntoAmount;
        this.numberOfRowsToExcludeHeader = record.numberOfRowsToExcludeHeader;
        this.numberOfRowsToExcludeFooter = record.numberOfRowsToExcludeFooter;
        this.textQualified = record.textQualified;
        this.fileFormatDelimited = record.fileFormatDelimited;
        this.fileFormatFixedWidth = record.fileFormatFixedWidth;
        this.fileFormatDelimitedType = record.fileFormatDelimitedType;
        this.useCustomTransactionCode = record.useCustomTransactionCode;
        this.mapColumn1 = record.mapColumn1;
        this.mapColumn2 = record.mapColumn2;
        this.mapColumn3 = record.mapColumn3;
        this.mapColumn4 = record.mapColumn4;
        this.mapColumn5 = record.mapColumn5;
        this.mapColumn6 = record.mapColumn6;
        this.mapColumn7 = record.mapColumn7;
        this.mapColumn8 = record.mapColumn8;
        this.itemTypeMappingIssue = record.itemTypeMappingIssue;
        this.itemTypeMappingStop = record.itemTypeMappingStop;
        this.itemTypeMappingVoid = record.itemTypeMappingVoid;
        this.customItemTypeCodes = record.customItemTypeCodes;
        this.textQualifiers = record.textQualifiers;
    }

    set textQualifiers(value) { this._textQualifiers = value; }

    /**
     * @name id
     * @instance
     * @property set {STRING} - sets the id
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set id(value) { this._id = value; }

    /**
     * @name positivePayCheckFile
     * @instance
     * @property set {STRING} - sets the positivePayCheckFile
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set positivePayCheckFile(value) { this._positivePayCheckFile = value; }

    /**
     * @name importAttachedFile
     * @instance
     * @property set {STRING} - sets the importAttachedFile
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set importAttachedFile(value) { this._importAttachedFile = value; }

    /**
     * @name name
     * @instance
     * @property set {STRING} - sets the name
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set name(value) { this._name = value; }

    /**
     * @name inputDecimalsIntoAmount
     * @instance
     * @property set {STRING} - sets the inputDecimalsIntoAmount
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set inputDecimalsIntoAmount(value) { this._inputDecimalsIntoAmount = value; }

    /**
     * @name numberOfRowsToExcludeHeader
     * @instance
     * @property set {STRING} - sets the numberOfRowsToExcludeHeader
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set numberOfRowsToExcludeHeader(value) { this._numberOfRowsToExcludeHeader = value; }

    /**
     * @name numberOfRowsToExcludeFooter
     * @instance
     * @property set {STRING} - sets the numberOfRowsToExcludeFooter
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set numberOfRowsToExcludeFooter(value) { this._numberOfRowsToExcludeFooter = value; }

    /**
     * @name textQualified
     * @instance
     * @property set {STRING} - sets the textQualified
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set textQualified(value) { this._textQualified = value; }

    /**
     * @name fileFormatDelimited
     * @instance
     * @property set {STRING} - sets the fileFormatDelimited
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set fileFormatDelimited(value) { this._fileFormatDelimited = value; }

    /**
     * @name fileFormatFixedWidth
     * @instance
     * @property set {STRING} - sets the fileFormatFixedWidth
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set fileFormatFixedWidth(value) { this._fileFormatFixedWidth = value; }

    /**
     * @name fileFormatDelimitedType
     * @instance
     * @property set {STRING} - sets the fileFormatDelimitedType
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set fileFormatDelimitedType(value) { this._fileFormatDelimitedType = value; }

    /**
     * @name useCustomTransactionCode
     * @instance
     * @property set {STRING} - sets the fileFormatDelimitedType
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set useCustomTransactionCode(value) { this._useCustomTransactionCode = value; }

    /**
     * @name mapColumn1
     * @instance
     * @property set {STRING} - sets the mapColumn1
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn1(value) { this._mapColumn1 = value; }

    /**
     * @name mapColumn2
     * @instance
     * @property set {STRING} - sets the mapColumn2
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn2(value) { this._mapColumn2 = value; }

    /**
     * @name mapColumn3
     * @instance
     * @property set {STRING} - sets the mapColumn3
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn3(value) { this._mapColumn3 = value; }

    /**
     * @name mapColumn4
     * @instance
     * @property set {STRING} - sets the mapColumn4
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn4(value) { this._mapColumn4 = value; }

    /**
     * @name mapColumn5
     * @instance
     * @property set {STRING} - sets the mapColumn5
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn5(value) { this._mapColumn5 = value; }

    /**
     * @name mapColumn6
     * @instance
     * @property set {STRING} - sets the mapColumn6
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn6(value) { this._mapColumn6 = value; }

    /**
     * @name mapColumn7
     * @instance
     * @property set {STRING} - sets the mapColumn7
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn7(value) { this._mapColumn7 = value; }

    /**
     * @name mapColumn8
     * @instance
     * @property set {STRING} - sets the mapColumn8
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set mapColumn8(value) { this._mapColumn8 = value; }

    /**
     * @name itemTypeMappingIssue
     * @instance
     * @property set {STRING} - sets the itemTypeMappingIssue
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set itemTypeMappingIssue(value) { this._itemTypeMappingIssue = value; }

    /**
     * @name itemTypeMappingStop
     * @instance
     * @property set {STRING} - sets the itemTypeMappingStop
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set itemTypeMappingStop(value) { this._itemTypeMappingStop = value; }

    /**
     * @name itemTypeMappingVoid
     * @instance
     * @property set {STRING} - sets the itemTypeMappingVoid
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    set itemTypeMappingVoid(value) { this._itemTypeMappingVoid = value; }

    set customItemTypeCodes(value) { this._customItemTypeCodes = value; }

    get textQualifiers() { return this._textQualifiers; }
    /**
     * @name id
     * @instance
     * @property get {STRING} - returns the id
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get id() { return this._id; }

    /**
     * @name positivePayCheckFile
     * @instance
     * @property get {STRING} - returns the positivePayCheckFile
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get positivePayCheckFile() { return this._positivePayCheckFile; }

    /**
     * @name importAttachedFile
     * @instance
     * @property get {STRING} - returns the importAttachedFile
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get importAttachedFile() { return this._importAttachedFile; }

    /**
     * @name name
     * @instance
     * @property get {STRING} - returns the name
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get name() { return this._name; }

    /**
     * @name inputDecimalsIntoAmount
     * @instance
     * @property get {STRING} - returns the inputDecimalsIntoAmount
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get inputDecimalsIntoAmount() { return this._inputDecimalsIntoAmount; }

    /**
     * @name numberOfRowsToExcludeHeader
     * @instance
     * @property get {STRING} - returns the numberOfRowsToExcludeHeader
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get numberOfRowsToExcludeHeader() { return this._numberOfRowsToExcludeHeader; }

    /**
     * @name numberOfRowsToExcludeFooter
     * @instance
     * @property get {STRING} - returns the numberOfRowsToExcludeFooter
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get numberOfRowsToExcludeFooter() { return this._numberOfRowsToExcludeFooter; }

    /**
     * @name textQualified
     * @instance
     * @property get {STRING} - returns the textQualified
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get textQualified() { return this._textQualified; }

    /**
     * @name fileFormatDelimited
     * @instance
     * @property get {STRING} - returns the fileFormatDelimited
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get fileFormatDelimited() { return this._fileFormatDelimited; }

    /**
     * @name fileFormatFixedWidth
     * @instance
     * @property get {STRING} - returns the fileFormatFixedWidth
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get fileFormatFixedWidth() { return this._fileFormatFixedWidth; }

    /**
     * @name fileFormatDelimitedType
     * @instance
     * @property get {STRING} - returns the fileFormatDelimitedType
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get fileFormatDelimitedType() { return this._fileFormatDelimitedType; }

    /**
     * @name useCustomTransactionCode
     * @instance
     * @property get {STRING} - returns the fileFormatDelimitedType
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get useCustomTransactionCode() { return this._useCustomTransactionCode; }

    /**
     * @name mapColumn1
     * @instance
     * @property get {STRING} - returns the mapColumn1
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn1() { return this._mapColumn1; }

    /**
     * @name mapColumn2
     * @instance
     * @property get {STRING} - returns the mapColumn2
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn2() { return this._mapColumn2; }

    /**
     * @name mapColumn3
     * @instance
     * @property get {STRING} - returns the mapColumn3
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn3() { return this._mapColumn3; }

    /**
     * @name mapColumn4
     * @instance
     * @property get {STRING} - returns the mapColumn4
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn4() { return this._mapColumn4; }

    /**
     * @name mapColumn5
     * @instance
     * @property get {STRING} - returns the mapColumn5
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn5() { return this._mapColumn5; }

    /**
     * @name mapColumn6
     * @instance
     * @property get {STRING} - returns the mapColumn6
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn6() { return this._mapColumn6; }

    /**
     * @name mapColumn7
     * @instance
     * @property get {STRING} - returns the mapColumn7
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn7() { return this._mapColumn7; }

    /**
     * @name mapColumn8
     * @instance
     * @property get {STRING} - returns the mapColumn8
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get mapColumn8() { return this._mapColumn8; }

    /**
     * @name itemTypeMappingIssue
     * @instance
     * @property get {STRING} - returns the itemTypeMappingIssue
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get itemTypeMappingIssue() { return this._itemTypeMappingIssue; }

    /**
     * @name itemTypeMappingStop
     * @instance
     * @property get {STRING} - returns the itemTypeMappingStop
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get itemTypeMappingStop() { return this._itemTypeMappingStop; }

    /**
     * @name itemTypeMappingVoid
     * @instance
     * @property get {STRING} - returns the itemTypeMappingVoid
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get itemTypeMappingVoid() { return this._itemTypeMappingVoid; }

    get customItemTypeCodes() { return this._customItemTypeCodes; }

    /**
     * @name allMapColumnsAreSelected
     * @instance
     * @property get {BOOLEAN} - returns true if all map columns are selected, false otherwise.
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get allMapColumnsAreSelected() {
        return Object.keys(this)
        .filter(key => key.includes("_mapColumn"))
        .reduce((acc, key) => {
            return acc && this[key] !== "(Exclude Column)";
        }, true);
    }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && (key !== "ranges" && key !== "types")){
                result[key] = value;
            }

            return result;
        }, {});
    }*/

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*checkValue(transaction, key) {
        switch(key) {
            case "date":
                return transaction.date.isAfter(BCMSDate.init().offset(this.date));
    
            case "description":
                return transaction.description.toLowerCase().indexOf(this.description.toLowerCase()) > -1;
    
            case "type":
                return this.type === "all" ? true : transaction.filterType === this.type;
    
            case "amount":
                const amount = parseFloat(transaction.amount.amount);
                return parseFloat(this.amount.amount) === (amount < 0 ? -amount : amount);
    
            case "check":
                const desc = transaction.description;
                return desc.toLowerCase().indexOf("check") > -1 && desc.indexOf(this.check) > -1;
    
            default:
                return true;
        }
    }*/

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*filter() {
        return Transaction.initByAccount(this.accountID)
            .filter(transaction => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(transaction, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }*/

    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayCreateCheckTemplateForm
     */
    get record() {
        return {
            id: this.id,
            positivePayCheckFile: this.positivePayCheckFile,
            importAttachedFile: this.importAttachedFile,
            name: this.name,
            inputDecimalsIntoAmount: this.inputDecimalsIntoAmount,
            numberOfRowsToExcludeHeader: this.numberOfRowsToExcludeHeader,
            numberOfRowsToExcludeFooter: this.numberOfRowsToExcludeFooter,
            textQualified: this.textQualified,
            fileFormatDelimited: this.fileFormatDelimited,
            fileFormatFixedWidth: this.fileFormatFixedWidth,
            fileFormatDelimitedType: this.fileFormatDelimitedType,
            useCustomTransactionCode: this.useCustomTransactionCode,
            mapColumn1: this.mapColumn1,
            mapColumn2: this.mapColumn2,
            mapColumn3: this.mapColumn3,
            mapColumn4: this.mapColumn4,
            mapColumn5: this.mapColumn5,
            mapColumn6: this.mapColumn6,
            mapColumn7: this.mapColumn7,
            mapColumn8: this.mapColumn8,
            itemTypeMappingIssue: this.itemTypeMappingIssue,
            itemTypeMappingStop: this.itemTypeMappingStop,
            itemTypeMappingVoid: this.itemTypeMappingVoid,
            customItemTypeCodes: this.customItemTypeCodes,
            textQualifiers: this.textQualifiers,
        };
    }

    static defaultValues = {
        id: "0",
        positivePayCheckFile: "",
        importAttachedFile: "false",
        name: "",
        inputDecimalsIntoAmount: "no",
        numberOfRowsToExcludeHeader: "",
        numberOfRowsToExcludeFooter: "",
        textQualified: "none",
        fileFormatDelimited: "delimited",
        fileFormatFixedWidth: "",
        fileFormatDelimitedType: "comma",
        useCustomTransactionCode: "",
        mapColumn1: "(Exclude Column)",
        mapColumn2: "(Exclude Column)",
        mapColumn3: "(Exclude Column)",
        mapColumn4: "(Exclude Column)",
        mapColumn5: "(Exclude Column)",
        mapColumn6: "(Exclude Column)",
        mapColumn7: "(Exclude Column)",
        mapColumn8: "(Exclude Column)",
        itemTypeMappingIssue: "",
        itemTypeMappingStop: "",
        itemTypeMappingVoid: "",
        customItemTypeCodes: [],
        textQualifiers: [
            { id: "0", label: `"`, value: `quote` },
            { id: "1", label: `'`, value: `apostrophe` },
            { id: "2", label: `None`, value: `none` }
        ]
    }

    reset() {
        return TASPositivePayCreateCheckTemplateForm.init(this.defaultValues);
    }
    
    static init(record) {
        const vals = { 
            ...this.defaultValues,
            ...record ?? {} 
        }
        return new TASPositivePayCreateCheckTemplateForm(vals);
    }
}